/**
 * PAGE-SHELL canonical values — the single source for the page-chrome scale,
 * and (in the second block) for the content-rhythm scale one layer below it.
 *
 * The page-chrome values are the owner-ruled values of `DECISIONS.md` D-144,
 * restated once here so `PageHeader`, `SectionHeader` and `PageBody` cannot
 * drift from each other (the drift class this family exists to kill: four
 * rendered title sizes across 40 route pages, because every page carried its
 * own literal).
 *
 * Normative source: SPEC-FRONTEND-RULE-061 (page chrome) and `DECISIONS.md`
 * D-149 (content rhythm). Do NOT re-type any of these values at a call site —
 * that is the violation.
 */

/** Page title: 1.5rem / 600. Ruled D-144(i). */
export const PAGE_TITLE_FONT_SIZE = '1.5rem' as const;

/** Section (panel) title: 1.25rem / 600 — one step below the page title. */
export const SECTION_TITLE_FONT_SIZE = '1.25rem' as const;

/**
 * Card-level title: 0.9375rem / 600 — the `size="card"` step of `SectionHeader`,
 * for a heading INSIDE a content surface rather than over a whole panel.
 *
 * This is the scale `SettingsCard` already rendered before D-149(d) reconciled
 * the two rival subordinate-heading components; it is preserved verbatim as a
 * variant of the survivor so the reconciliation is STRUCTURAL (one component
 * owns both steps) and does not silently restyle the 26 existing call sites.
 * Ruled D-149(d) — proposed.
 */
export const CARD_TITLE_FONT_SIZE = '0.9375rem' as const;

/** Shared by both title levels. */
export const TITLE_FONT_WEIGHT = 600 as const;

/** Titles wrap rather than truncate, so a long Hungarian title stays legible (RULE-047). */
export const TITLE_LINE_HEIGHT = 1.3 as const;

/** Gap between a title and the description beneath it, in theme spacing units. */
export const TITLE_DESCRIPTION_GAP = 0.5 as const;

/** Page-header padding, in theme spacing units: 24px horizontal, 16px vertical. */
export const HEADER_PADDING_X = 3 as const;
export const HEADER_PADDING_Y = 2 as const;

/** The uniform content gutter, in theme spacing units (24px). Ruled D-144(iii). */
export const BODY_PADDING = 3 as const;

/** Horizontal gap between the title block and the actions slot. */
export const HEADER_ACTIONS_GAP = 2 as const;

// ─── CONTENT RHYTHM ────────────────────────────────────────────────────────
//
// The layer BELOW the page chrome: the bordered content groups inside a page,
// and the vertical beat between them. Same drift class, one level down —
// measured on `origin/main` 2026-08-26 across `web/src/pages/tenant/**` +
// `web/src/plugins/**`: 4 rival surface treatments (46 `<Card>` incl. 1
// outlined, 36 `<Paper>` incl. 9 outlined — 82 elements across 41 files),
// 7 corner radii, 9 vertical gaps and 8 surface paddings.
//
// Values are `DECISIONS.md` D-149(b)(c). They are PROPOSED, not yet ruled;
// because they live here and nowhere else, an owner override is a one-token
// edit rather than an 82-site sweep. That single-module property IS the
// argument for this block — it is the reason the D-144 header values above
// have not drifted since they landed.

/**
 * Gap between two stacked content blocks, in theme spacing units (24px).
 *
 * Deliberately the SAME measure as {@link BODY_PADDING}, so the page gutter,
 * the gap between blocks and the padding inside a block are one rhythm rather
 * than three. Note this is NOT the most common value in the tree today
 * (`mb: 2` leads at 247 sites vs `mb: 3` at 186) — the argument is alignment
 * with the page gutter, not popularity. Proposed D-149(c).
 */
export const CONTENT_BLOCK_GAP = 3 as const;

/**
 * Padding inside a content surface, in theme spacing units (24px).
 * Matches {@link CONTENT_BLOCK_GAP} and {@link BODY_PADDING}. Proposed D-149(c).
 */
export const SURFACE_PADDING = 3 as const;

/**
 * Corner radius of a content surface: 8px.
 *
 * NOTE the unit — in an MUI `sx`, `borderRadius` is a multiple of
 * `theme.shape.borderRadius` (4px), NOT of `theme.spacing` (8px). So `2`
 * renders 8px. Every other token in this module is in spacing units; this one
 * is not, and confusing the two silently doubles the radius.
 *
 * One value replacing seven. D-149(b) PROPOSED `1.5` (6px) on the grounds that
 * it is the joint-most-used radius in the tree (76 sites, tied with `1`) — but
 * that is a popularity argument, and this fork is aesthetic. On 2026-08-26 the
 * owner compared the Training and Job History pages, chose the Job History
 * filter bar ("more modern and cleaner"), and that surface renders `2`. An
 * aesthetic fork is settled by the owner, not by a census, so the ruled value
 * is `2`.
 */
export const SURFACE_RADIUS = 2 as const;

/**
 * Background of a `dangerZone` content surface.
 *
 * Lifted out of an inline hex literal in `SettingsCard` (RULE-034 tokens-only).
 * It lives here rather than in `theme/colors.ts` because it is a content-SURFACE
 * value and this module is the content-surface scale; when the dark-mode token
 * layer lands it moves into the palette together with the three other `#FEF2F2`
 * sites still inlined in `ChatWidget`, `CrawlErrorList` and `ExplorerItem`
 * (part of the 471-inline-hex backlog deliberately carved out of this work).
 */
export const SURFACE_DANGER_BG = '#FEF2F2' as const;
